import { Link } from "react-router-dom";
import { Flame, Loader2, Trophy, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useQuest, useLeft, endLabel, QUEST_ICON } from "@/lib/quest";
import { QuestGoals } from "@/components/quest/QuestGoals";
import { QuestHistory } from "@/components/quest/QuestHistory";
import { QuestGallery } from "@/components/quest/QuestGallery";
import { QuestRace } from "@/components/quest/QuestRace";
import { Medal } from "lucide-react";
import { UserAvatar, UserLink } from "@/components/UserBits";

const HOW = { comment: ["Kommentiere Beiträge", "/feed"], like: ["Like Beiträge", "/feed"], photo: ["Poste ein Foto", "/feed"], post: ["Schreib einen Beitrag", "/feed"], invite: ["Lade Freunde ein", "/einladen"] };

function Countdown({ end }) {
  const { left, text } = useLeft(end);
  return <p className="font-display text-5xl sm:text-6xl font-extrabold tabular-nums drop-shadow" data-testid="quest-timer">{left > 0 ? text : "00:00:00"}</p>;
}

export default function Quest() {
  const [q] = useQuest(30000);
  const { user } = useAuth();
  if (q === null) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  if (!q) return <p className="text-center py-20 text-slate-400">Gerade läuft keine Community-Aufgabe.</p>;
  const mineTotal = Object.values(q.mine).reduce((a, b) => a + b, 0);
  if (q.excluded) return (
    <div className="max-w-xl mx-auto rounded-3xl bg-white border border-red-100 p-8 text-center space-y-2" data-testid="quest-excluded">
      <p className="font-display text-2xl font-extrabold">Community-Aufgabe</p>
      <p className="text-slate-600">Du bist von dieser Aktion ausgeschlossen. Deine Beiträge zählen hier nicht, und es werden dir keine Punkte gutgeschrieben oder abgezogen.</p>
    </div>
  );
  return (
    <div className="space-y-5 max-w-3xl mx-auto" data-testid="quest-page">
      <div className="quest-glow relative overflow-hidden rounded-[2rem] text-white p-6 sm:p-8 space-y-5">
        <span className="contest-shine" />
        <div className="relative space-y-2">
          <p className="text-xs font-extrabold uppercase tracking-[0.3em] flex items-center gap-1.5"><Flame className="w-4 h-4" /> Community-Aufgabe</p>
          <h1 className="font-display text-3xl sm:text-5xl font-extrabold leading-tight text-white" style={{ background: "none", WebkitTextFillColor: "#fff" }}>Nur gemeinsam schaffbar!</h1>
          <p className="text-red-50">Erreicht die Community bis {endLabel(q.end)} <b>{q.goals.length === 1 ? "das Ziel" : `alle ${q.goals.length} Ziele`}</b>, bekommt jeder Mitmacher <b>+{q.stake} Punkte</b>. Schaffen wir es nicht, verliert jeder Mitmacher <b>{q.stake} Punkte</b>.</p>
        </div>
        <div className="relative">
          {q.status === "running" ? <><p className="text-xs font-bold uppercase tracking-widest text-red-100">Verbleibende Zeit</p><Countdown end={q.end} /></> : (
            <p className="font-display text-3xl font-extrabold" data-testid="quest-result">{q.status === "ended" ? "Wird ausgewertet …" : q.success ? `Geschafft! 🎉 +${q.stake} Punkte für alle Mitmacher` : `Verpasst 💔 −${q.stake} Punkte für alle Mitmacher`}</p>
          )}
        </div>
        <div className="relative"><QuestGoals goals={q.goals} big /></div>
        <p className="relative text-sm font-semibold" data-testid="quest-participants">{q.participants} Mitglieder machen mit</p>
      </div>

      <QuestRace race={q.race} />

      <div className="rounded-3xl bg-white border border-red-100 p-5 space-y-3" data-testid="quest-mine">
        <p className="font-bold">Dein Beitrag</p>
        {mineTotal ? (
          <p className="text-sm text-slate-600">Du hast schon <b>{mineTotal}×</b> geholfen: {Object.entries(q.mine).map(([k, n]) => `${QUEST_ICON[k]} ${n}`).join(" · ")}. Du bist dabei – {q.status === "running" ? "jetzt heißt es zusammenhalten!" : "danke fürs Mitmachen!"}</p>
        ) : <p className="text-sm text-slate-600">Du machst noch nicht mit. Sobald du einmal kommentierst, likest, ein Foto postest oder einen Freund einlädst, bist du dabei – mit Chance auf +{q.stake} Punkte, aber auch mit Risiko.</p>}
        {q.status === "running" && (
          <div className="grid grid-cols-2 gap-2">
            {q.goals.map((g) => <Link key={g.kind} to={HOW[g.kind][1]} data-testid={`quest-how-${g.kind}`} className="rounded-2xl bg-red-50 text-[#b0002a] font-bold text-sm px-3 py-3 flex items-center gap-2 hover:bg-red-100">{QUEST_ICON[g.kind]} {HOW[g.kind][0]}</Link>)}
          </div>
        )}
      </div>

      <div className="rounded-3xl bg-white border border-red-100 overflow-hidden">
        <p className="p-5 pb-2 font-display text-xl font-extrabold flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Die fleißigsten Helfer</p>
        {q.top.length ? (
          <ol className="divide-y divide-red-50" data-testid="quest-top">
            {q.top.map((t, i) => (
              <li key={t.user_id} data-testid={`quest-top-${i + 1}`} className={`flex items-center gap-3 p-4 ${t.user_id === user?.user_id ? "bg-red-50" : i < 3 && q.status === "running" ? "bg-amber-50/60" : ""}`}>
                <span className="w-6 text-center font-extrabold text-slate-400">{i < 3 && q.status === "running" ? <Medal className={`w-5 h-5 mx-auto ${["text-amber-500", "text-slate-400", "text-orange-500"][i]}`} /> : i + 1}</span>
                <UserAvatar name={t.name} picture={t.picture} userId={t.user_id} />
                <p className="flex-1 font-semibold truncate"><UserLink id={t.user_id}>{t.name}</UserLink></p>
                <span className="font-display font-extrabold text-[#d2042d]">{t.total}×</span>
              </li>
            ))}
          </ol>
        ) : <p className="p-8 text-center text-sm text-slate-400">Noch niemand dabei – sei die/der Erste!</p>}
        {q.status === "running" && q.top.length > 0 && <p className="px-5 pb-4 text-xs text-amber-700 font-semibold flex items-center gap-1.5" data-testid="quest-top-hint"><Medal className="w-3.5 h-3.5" /> Die Top 3 werden Aufgaben-Helden, wenn die Aufgabe geschafft wird.</p>}
      </div>

      <QuestGallery />
      <QuestHistory />

      <div className="rounded-3xl bg-white border border-red-100 p-5 space-y-2 text-sm text-slate-600">
        {["Jeder Kommentar, jedes Like, jedes Foto und jeder neu eingeladene Freund zählt zum gemeinsamen Fortschritt.", "Nur alle Ziele zusammen zählen – fehlt eins, ist die Aufgabe verpasst.", `Mitmacher ist, wer seit dem Start mindestens einmal beigetragen hat. Nur Mitmacher gewinnen oder verlieren die ${q.stake} Punkte (auch ins Minus).`].map((t) => <p key={t} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#d2042d] shrink-0 mt-0.5" /> {t}</p>)}
      </div>
    </div>
  );
}
